import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MapPin, Copy, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const BulkLocationCloner: React.FC = () => {
  const [isCloning, setIsCloning] = useState(false);
  const [results, setResults] = useState<any>(null);

  const handleBulkClone = async () => {
    setIsCloning(true);
    setResults(null);

    try {
      console.log('Starting bulk clone operation...');
      
      const { data, error } = await supabase.functions.invoke('bulk-clone-posts', {
        body: {}
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (data.success) {
        setResults(data);
        toast.success(`Successfully created ${data.details.postsCreated} blog posts for missing locations!`);
      } else {
        throw new Error(data.error || 'Unknown error occurred');
      }

    } catch (error) {
      console.error('Error during bulk cloning:', error);
      toast.error(`Failed to clone posts: ${error.message}`);
      setResults({ 
        success: false, 
        error: error.message,
        details: { postsCreated: 0, errors: 1 }
      });
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Copy className="h-5 w-5 text-blue-500" />
          Bulk Location Blog Cloner
        </CardTitle>
        <CardDescription>
          Automatically create blog posts for all locations missing content. This will clone your main 14 blog posts to the 64 locations that don't have any posts yet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {!results && (
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                What this will do:
              </h4>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Identify locations without blog posts (approximately 64 locations)</li>
                <li>Use your main 14 blog post templates</li>
                <li>Create location-specific versions with proper variables replaced</li>
                <li>Generate SEO-optimized URLs and tags</li>
                <li>Publish posts immediately for better search coverage</li>
              </ul>
            </div>

            <Button 
              onClick={handleBulkClone}
              disabled={isCloning}
              className="w-full"
              size="lg"
            >
              {isCloning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cloning Posts for Missing Locations...
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Clone Posts to All Missing Locations
                </>
              )}
            </Button>
          </div>
        )}

        {results && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${results.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                {results.success ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                )}
                <h4 className="font-semibold">
                  {results.success ? 'Bulk Clone Completed!' : 'Bulk Clone Failed'}
                </h4>
              </div>
              
              <p className="text-sm mb-3">{results.message}</p>
              
              {results.details && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Posts Created:</span>
                    <span className="ml-2 text-green-600">{results.details.postsCreated}</span>
                  </div>
                  <div>
                    <span className="font-medium">Locations Processed:</span>
                    <span className="ml-2">{results.details.locationsProcessed}</span>
                  </div>
                  <div>
                    <span className="font-medium">Template Posts Used:</span>
                    <span className="ml-2">{results.details.templatePostsUsed}</span>
                  </div>
                  <div>
                    <span className="font-medium">Errors:</span>
                    <span className="ml-2 text-red-600">{results.details.errors}</span>
                  </div>
                </div>
              )}

              {results.details?.sampleClonedPosts && results.details.sampleClonedPosts.length > 0 && (
                <div className="mt-4">
                  <h5 className="font-medium text-sm mb-2">Sample Created Posts:</h5>
                  <div className="space-y-1 text-xs max-h-32 overflow-y-auto">
                    {results.details.sampleClonedPosts.map((post: any, index: number) => (
                      <div key={index} className="flex justify-between">
                        <span className="font-medium">{post.location}:</span>
                        <span className="text-muted-foreground">{post.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Button 
              onClick={() => {
                setResults(null);
                window.location.reload(); // Refresh to see updated posts
              }}
              variant="outline"
              className="w-full"
            >
              Refresh Page to See Updated Posts
            </Button>
          </div>
        )}

        <div className="text-sm text-muted-foreground p-3 bg-yellow-50 rounded-md">
          <p className="font-medium mb-1">⚠️ Important Notes:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>This operation will create approximately 896 new blog posts (64 locations × 14 posts)</li>
            <li>All posts will be published immediately with location-specific content</li>
            <li>Each post gets unique URLs for better SEO performance</li>
            <li>Process may take 2-3 minutes to complete</li>
            <li>You can run this multiple times safely - it won't create duplicates</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkLocationCloner;